/**
 * TECH-JOB-SCREEN §8 item 1 — auto-update, safely.
 *
 * iPads run stale PWA bundles for days because techs never tap the
 * "New version — Reload" toast mid-job. The gate closes that hole:
 *
 *   new build + NO dirty form state → activate + reload silently
 *   dirty form state → sticky banner ("Update ready — applies after you
 *   save") and the update self-applies at the next safe moment: a
 *   workflow save success or navigation back to the device/job list —
 *   both call notifySafeMoment().
 *
 * "Dirty" is decided by providers the active screens register (the
 * backflow workflow registers its unsaved-draft set). Screens without a
 * provider are clean by definition, so office tabs keep silently
 * auto-updating.
 */

type DirtyProvider = () => boolean;

const providers = new Set<DirtyProvider>();
let pendingApply: (() => void) | null = null;

/** Fired when an update is ready but form state is dirty — the sticky
 *  banner listens for this. */
export const UPDATE_DEFERRED_EVENT = "sw-update-deferred";

export function registerDirtyProvider(fn: DirtyProvider): () => void {
    providers.add(fn);
    return () => providers.delete(fn);
}

export function hasDirtyFormState(): boolean {
    for (const fn of providers) {
        try {
            if (fn()) return true;
        } catch {
            // A throwing provider must never block updates.
        }
    }
    return false;
}

export function hasDeferredUpdate(): boolean {
    return pendingApply !== null;
}

/**
 * A new build is installed and waiting. Returns what happened so the
 * caller (sw-register) can log it.
 */
export function updateAvailable(apply: () => void): "applied" | "deferred" {
    if (!hasDirtyFormState()) {
        pendingApply = null;
        apply();
        return "applied";
    }
    pendingApply = apply;
    window.dispatchEvent(new CustomEvent(UPDATE_DEFERRED_EVENT));
    return "deferred";
}

/**
 * A safe moment passed (workflow save landed, navigation to the list).
 * Applies the deferred update if the form state is clean now. Returns
 * true when the update was applied (the page is about to reload).
 */
export function notifySafeMoment(): boolean {
    if (!pendingApply) return false;
    if (hasDirtyFormState()) return false;
    const apply = pendingApply;
    pendingApply = null;
    apply();
    return true;
}

/** Force-apply regardless of dirty state — the banner's explicit tap. */
export function applyDeferredNow(): boolean {
    if (!pendingApply) return false;
    const apply = pendingApply;
    pendingApply = null;
    apply();
    return true;
}

/** Test-only reset. */
export function __resetUpdateGateForTests(): void {
    providers.clear();
    pendingApply = null;
}
